import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiMenu, FiX, FiPhone, FiMail, FiClock, FiTruck, FiFileText, FiMessageCircle } from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaWhatsapp, FaYoutube, FaSnowflake, FaCar, FaTractor } from 'react-icons/fa';
import BrandScroller from './BrandScroller';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showMegaMenu, setShowMegaMenu] = useState(false);
    const [megaMenuTimeout, setMegaMenuTimeout] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        // Kategori isimlerini tanı
        const categories = ['otomobil', 'suv', 'van', 'minibüs', 'minibus', 'kamyonet', 'kamyon', 'otobüs', 'otobus', 'ticari', '4x4', 'traktör', 'traktor', 'iş makinesi', 'is makinesi'];
        const searchLower = searchQuery.toLowerCase().trim();

        // Eğer arama kategori ismiyse, category parametresiyle yönlendir
        const matchedCategory = categories.find(cat => searchLower.includes(cat));

        if (matchedCategory) {
            // Kategori adını düzelt (Türkçe karakterler)
            let categoryName = matchedCategory.charAt(0).toUpperCase() + matchedCategory.slice(1);
            if (categoryName === 'Minibus') categoryName = 'Minibüs';
            if (categoryName === 'Otobus') categoryName = 'Otobüs';
            if (categoryName === 'Ticari') categoryName = 'Ticari';
            if (categoryName === 'Otomobil') categoryName = 'Otomobil';
            if (categoryName === 'Suv') categoryName = 'SUV';
            if (categoryName === 'Van') categoryName = 'Van';
            if (categoryName === 'Kamyonet') categoryName = 'Kamyonet';
            if (categoryName === 'Kamyon') categoryName = 'Kamyon';
            if (categoryName === '4x4') categoryName = '4x4';
            if (categoryName === 'Traktör' || categoryName === 'Traktor') categoryName = 'Traktör Lastikleri';
            if (categoryName === 'Iş makinesi' || categoryName === 'Is makinesi') categoryName = 'İş Makinesi Lastikleri';

            navigate(`/lastik?category=${encodeURIComponent(categoryName)}`);
        } else {
            // Normal arama
            navigate(`/lastik?search=${encodeURIComponent(searchQuery)}`);
        }

        setSearchQuery('');
        setShowSuggestions(false);
    };

    const handleSearchInput = async (value: string) => {
        // Sondaki boşlukları temizle (copy-paste durumları için)
        const cleanedValue = value.trim();
        setSearchQuery(cleanedValue);
        setSelectedIndex(-1);

        if (cleanedValue.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            // API'den autocomplete önerileri al
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/products/search-suggestions?q=${encodeURIComponent(value)}`);
            if (response.ok) {
                const data = await response.json();
                setSuggestions(data.suggestions || []);
                setShowSuggestions(data.suggestions && data.suggestions.length > 0);
            }
        } catch (error) {
            console.error('Autocomplete error:', error);
            // Fallback: Yerel öneriler
            const localSuggestions = generateLocalSuggestions(value);
            setSuggestions(localSuggestions);
            setShowSuggestions(localSuggestions.length > 0);
        }
    };

    const generateLocalSuggestions = (query: string): any[] => {
        const brands = ['Michelin', 'Bridgestone', 'Goodyear', 'Continental', 'Pirelli', 'Dunlop',
            'Hankook', 'Yokohama', 'Lassa', 'Petlas', 'Falken', 'Kumho'];
        const sizes = ['205/55R16', '195/65R15', '225/45R17', '215/60R16', '185/65R15'];
        const types = ['Kış lastiği', 'Yaz lastiği', 'Dört mevsim', 'Kamyon lastiği', 'İş makinesi lastiği'];
        const categories = ['Otomobil', 'SUV', 'Van', 'Minibüs', 'Kamyonet', 'Kamyon', 'Otobüs', 'Ticari', '4x4', 'Traktör Lastikleri', 'İş Makinesi Lastikleri'];

        const lowerQuery = query.toLowerCase();
        const suggestions: any[] = [];

        // Kategori önerileri
        categories.forEach(category => {
            if (category.toLowerCase().includes(lowerQuery)) {
                suggestions.push({ value: category, label: `${category} Lastikleri`, type: 'category' });
            }
        });

        brands.forEach(brand => {
            if (brand.toLowerCase().includes(lowerQuery)) {
                suggestions.push({ value: brand, label: `${brand} - Marka`, type: 'brand' });
            }
        });

        sizes.forEach(size => {
            if (size.toLowerCase().includes(lowerQuery)) {
                suggestions.push({ value: size, label: `${size} - Ebat`, type: 'tire_size' });
            }
        });

        types.forEach(type => {
            if (type.toLowerCase().includes(lowerQuery)) {
                suggestions.push({ value: type, label: type, type: 'product' });
            }
        });

        return suggestions.slice(0, 8);
    };

    const handleSuggestionClick = (suggestion: any) => {
        const searchValue = typeof suggestion === 'string' ? suggestion : suggestion.value;
        setSearchQuery('');
        setShowSuggestions(false);

        // Kategori isimlerini tanı
        const categories = ['otomobil', 'suv', 'van', 'minibüs', 'minibus', 'kamyonet', 'kamyon', 'otobüs', 'otobus', 'ticari', '4x4', 'traktör', 'traktor', 'iş makinesi', 'is makinesi'];
        const searchLower = searchValue.toLowerCase().trim();

        // Eğer arama kategori ismiyse, category parametresiyle yönlendir
        const matchedCategory = categories.find(cat => searchLower.includes(cat));

        if (matchedCategory) {
            // Kategori adını düzelt (Türkçe karakterler)
            let categoryName = matchedCategory.charAt(0).toUpperCase() + matchedCategory.slice(1);
            if (categoryName === 'Minibus') categoryName = 'Minibüs';
            if (categoryName === 'Otobus') categoryName = 'Otobüs';
            if (categoryName === 'Ticari') categoryName = 'Ticari';
            if (categoryName === 'Otomobil') categoryName = 'Otomobil';
            if (categoryName === 'Suv') categoryName = 'SUV';
            if (categoryName === 'Van') categoryName = 'Van';
            if (categoryName === 'Kamyonet') categoryName = 'Kamyonet';
            if (categoryName === 'Kamyon') categoryName = 'Kamyon';
            if (categoryName === '4x4') categoryName = '4x4';
            if (categoryName === 'Traktör' || categoryName === 'Traktor') categoryName = 'Traktör Lastikleri';
            if (categoryName === 'Iş makinesi' || categoryName === 'Is makinesi') categoryName = 'İş Makinesi Lastikleri';

            navigate(`/lastik?category=${encodeURIComponent(categoryName)}`);
        } else {
            // Normal arama
            navigate(`/lastik?search=${encodeURIComponent(searchValue)}`);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions || suggestions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            handleSuggestionClick(suggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    // Mega Menu İçeriği - SEO için popüler linkler
    const megaMenuData = {
        categories: [
            { name: 'Otomobil', path: '/lastik?category=Otomobil', icon: '🚗' },
            { name: 'SUV', path: '/lastik?category=SUV', icon: '🚙' },
            { name: 'Kamyonet', path: '/lastik?category=Kamyonet', icon: '🚚' },
            { name: '4x4', path: '/lastik?category=4x4', icon: '🏔️' },
            { name: 'Van', path: '/lastik?category=Van', icon: '🚐' },
            { name: 'Kamyon', path: '/kamyon-lastikleri', icon: '🚛' },
            { name: 'Minibüs', path: '/lastik?category=Minibüs', icon: '🚌' },
            { name: 'Otobüs', path: '/lastik?category=Otobüs', icon: '🚍' },
            { name: 'Traktör Lastikleri', path: '/lastik?category=Traktör Lastikleri', icon: '🚜' },
            { name: 'İş Makinesi Lastikleri', path: '/lastik?category=İş Makinesi Lastikleri', icon: '🏗️' },
        ],
        seasons: [
            { name: 'Kış Lastikleri', path: '/lastik?season=Kış', icon: '❄️' },
            { name: 'Yaz Lastikleri', path: '/lastik?season=Yaz', icon: '☀️' },
            { name: '4 Mevsim Lastikleri', path: '/lastik?season=4 Mevsim', icon: '🌤️' },
        ],
        brands: [
            { name: 'Michelin', path: '/lastik?brand=Michelin' },
            { name: 'Bridgestone', path: '/lastik?brand=Bridgestone' },
            { name: 'Goodyear', path: '/lastik?brand=Goodyear' },
            { name: 'Continental', path: '/lastik?brand=Continental' },
            { name: 'Pirelli', path: '/lastik?brand=Pirelli' },
            { name: 'Petlas', path: '/lastik?brand=Petlas' },
            { name: 'Lassa', path: '/products?brand=Lassa' },
            { name: 'Dunlop', path: '/products?brand=Dunlop' },
        ],
        popular: [
            { name: 'Michelin Kış Lastikleri', path: '/products?brand=Michelin&season=Kış' },
            { name: 'Bridgestone Yaz Lastikleri', path: '/products?brand=Bridgestone&season=Yaz' },
            { name: 'Petlas Otomobil Lastikleri', path: '/products?brand=Petlas&category=Otomobil' },
            { name: 'SUV Kış Lastikleri', path: '/products?category=SUV&season=Kış' },
            { name: 'Kamyon Lastikleri', path: '/products?category=Kamyon' },
            { name: 'Traktör Lastikleri', path: '/products?category=Traktör Lastikleri' },
            { name: '205/55R16 Lastikler', path: '/products?tire_size=205/55R16' },
        ]
    };

    const handleMegaMenuEnter = () => {
        if (megaMenuTimeout) {
            clearTimeout(megaMenuTimeout);
            setMegaMenuTimeout(null);
        }
        setShowMegaMenu(true);
    };

    const handleMegaMenuLeave = () => {
        const timeout = setTimeout(() => {
            setShowMegaMenu(false);
        }, 200);
        setMegaMenuTimeout(timeout);
    };

    const mainMenuItems = [
        {
            name: 'Kış Lastikleri',
            path: '/products?season=Kış',
            icon: <FaSnowflake className="inline" />
        },
        {
            name: 'Kamyon Lastikleri',
            path: '/kamyon-lastikleri',
            icon: <FiTruck className="inline" />
        },
        {
            name: 'Otomobil Lastikleri',
            path: '/products?category=Otomobil',
            icon: <FaCar className="inline" />
        },
        {
            name: 'İş Makinesi Lastikleri',
            path: '/products?category=İş Makinesi Lastikleri',
            icon: <FaTractor className="inline" />
        },
        { name: 'Blog', path: '/blog', icon: <FiFileText className="inline" /> },
        { name: 'İletişim', path: '/iletisim', icon: <FiMessageCircle className="inline" /> },
    ];

    const topMenuItems = [
        { name: 'Hakkımızda', path: '/hakkimizda' },
        { name: 'Kampanyalar', path: '/kampanyalar' },
        { name: 'Yardım', path: '/yardim' },
        { name: 'S.S.S', path: '/sss' },
    ];

    const socialLinks = [
        { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=100032739833072', icon: <FaFacebook /> },
        { name: 'Instagram', url: 'https://instagram.com/altunotolastik', icon: <FaInstagram /> },
        { name: 'Youtube', url: 'https://youtube.com', icon: <FaYoutube /> },
        { name: 'WhatsApp', url: 'https://wa.me/905441205454', icon: <FaWhatsapp /> },
    ];

    return (
        <header className="sticky top-0 z-50 shadow-sm bg-white">
            {/* Üst Bar - Rebecca Purple */}
            <div className="hidden lg:block bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 border-b border-purple-200 py-2.5">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center text-xs">
                        {/* Sol - Sosyal Medya */}
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1.5 text-purple-700 font-semibold">
                                <FiClock size={14} />
                                <span>7/24 Destek</span>
                            </span>
                            <div className="flex items-center gap-2 ml-2">
                                {socialLinks.map((social, index) => (
                                    <a
                                        key={index}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-purple-600 hover:text-purple-800 transition-colors transform hover:scale-110"
                                        title={social.name}
                                    >
                                        <span className="text-base">{social.icon}</span>
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Sağ - Menü ve İletişim */}
                        <div className="flex items-center gap-3 md:gap-5 ml-auto">
                            <div className="flex items-center gap-4">
                                {topMenuItems.map((item, index) => (
                                    <button
                                        key={index}
                                        onClick={() => navigate(item.path)}
                                        className="text-purple-700 hover:text-purple-900 hover:underline transition font-medium"
                                    >
                                        {item.name}
                                    </button>
                                ))}
                            </div>
                            <div className="h-4 w-px bg-purple-300"></div>
                            <a href="mailto:info@sakaryalastik.com" className="flex items-center gap-1.5 text-purple-700 hover:text-purple-900 transition font-medium">
                                <FiMail size={13} />
                                <span>Email</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Ana Header */}
            <div className="bg-white py-3 md:py-4 border-b-2 border-purple-100">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between gap-3 md:gap-6">
                        {/* Logo */}
                        <div className="cursor-pointer flex-shrink-0" onClick={() => navigate("/")}>
                            <img src="/images/logo.webp" alt="Sakarya Lastik" className="h-12 md:h-20 w-auto object-contain" />
                        </div>

                        {/* Search Bar */}
                        <div className="flex-1 max-w-2xl relative">
                            <form onSubmit={handleSearch} className="w-full">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => handleSearchInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                        onPaste={async (e) => {
                                            // Paste eventinde de trimle - Clipboard API ile güvenli
                                            try {
                                                const clipboardData = e.clipboardData;
                                                if (clipboardData) {
                                                    const pastedText = clipboardData.getData('text');
                                                    if (pastedText) {
                                                        const trimmedText = pastedText.trim();
                                                        // Eğer trim edilmiş versi farklıysa, input'u güncelle
                                                        if (trimmedText !== pastedText) {
                                                            e.preventDefault(); // Default paste'i engelle
                                                            handleSearchInput(trimmedText);
                                                        }
                                                    }
                                                }
                                            } catch (error) {
                                                console.log('Paste event error:', error);
                                                // Fallback - normal paste işlemi devam etsin
                                            }
                                        }}
                                        placeholder="Ürün, marka veya lastik ebatı ara (örn: 205/55R16, Michelin)"
                                        className="w-full px-4 py-2.5 md:py-3 pr-12 rounded-lg outline-none text-sm border-2 border-purple-200 focus:border-purple-400 transition shadow-sm"
                                        autoComplete="off"
                                        aria-label="Ürün arama"
                                    />
                                    <button
                                        type="submit"
                                        className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white p-2 md:p-2.5 rounded-md transition shadow-md z-10"
                                        aria-label="Ara"
                                        title="Ara"
                                    >
                                        <FiSearch size={18} aria-hidden="true" />
                                    </button>
                                </div>
                            </form>

                            {/* Autocomplete Suggestions */}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-purple-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                                    {suggestions.map((suggestion, index) => {
                                        const displayText = typeof suggestion === 'string' ? suggestion : suggestion.label;
                                        return (
                                            <div
                                                key={index}
                                                onClick={() => handleSuggestionClick(suggestion)}
                                                className={`px-4 py-3 cursor-pointer transition-colors flex items-center gap-2 ${selectedIndex === index
                                                    ? 'bg-purple-100 text-purple-700'
                                                    : 'hover:bg-purple-50 text-gray-700'
                                                    }`}
                                            >
                                                <FiSearch size={14} className="text-purple-400" />
                                                <span className="text-sm font-medium">{displayText}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* İletişim Bilgileri - Desktop */}
                        <div className="hidden lg:flex items-center gap-4">
                            {/* Telefon */}
                            <a href="tel:905441205454" className="flex items-center gap-2 text-purple-700 hover:text-purple-900 transition group">
                                <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-3 rounded-full group-hover:from-purple-200 group-hover:to-purple-300 transition shadow-md">
                                    <FiPhone size={20} className="text-purple-600" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-700 font-medium">Hemen Ara</span>
                                    <span className="font-bold text-base text-purple-800">0(544) 120-5454</span>
                                </div>
                            </a>

                            {/* Divider */}
                            <div className="h-12 w-px bg-gradient-to-b from-transparent via-purple-300 to-transparent"></div>

                            {/* 7/24 Yol Yardım */}
                            <div className="flex items-center gap-2 text-purple-700">
                                <div className="bg-gradient-to-br from-orange-100 to-orange-200 p-3 rounded-full shadow-md">
                                    <FiTruck size={20} className="text-orange-700" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs text-gray-700 font-medium">7/24 Yol Yardım</span>
                                    <span className="font-bold text-sm text-orange-700">Destek Hattı</span>
                                </div>
                            </div>
                        </div>

                        {/* Hamburger Menu - Mobile */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="lg:hidden p-2 rounded-lg hover:bg-purple-50 transition border-2 border-purple-200"
                            aria-label={isMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
                            aria-expanded={isMenuOpen}
                            aria-controls="mobile-menu"
                            title={isMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
                        >
                            <FiMenu size={24} className="text-purple-600" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Renkli Dekoratif Bar */}
            <div className="hidden lg:block overflow-hidden">
                <div className="flex w-full">
                    <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600" style={{ width: '600px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: '300px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-green-500 to-green-600" style={{ width: '220px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-red-500 to-red-600" style={{ width: '180px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-purple-500 to-purple-600" style={{ width: '250px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-yellow-500 to-yellow-600" style={{ width: '210px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-pink-500 to-pink-600" style={{ width: '270px' }}></div>
                    <div className="h-2 bg-gradient-to-r from-cyan-500 to-cyan-600" style={{ width: '200px' }}></div>
                </div>
            </div>

            {/* Alt Menü - Rebecca Purple Theme with Mega Menu */}
            <div className="hidden lg:block bg-gradient-to-r from-purple-50 via-white to-purple-50 border-b-2 border-purple-200 shadow-sm relative">
                <div className="container mx-auto px-4">
                    <nav className="flex items-center gap-6 py-1">
                        {/* Lastikler Mega Menu Trigger */}
                        <div
                            className="relative"
                            onMouseEnter={handleMegaMenuEnter}
                            onMouseLeave={handleMegaMenuLeave}
                        >
                            <button
                                className="text-sm transition relative flex items-center gap-2 px-3 py-2 rounded-md text-blue-700 font-medium hover:bg-blue-50"
                            >
                                <FaCar className="text-lg" />
                                Lastikler
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Mega Menu Dropdown */}
                            {showMegaMenu && (
                                <div className="absolute top-full left-0 mt-1 w-screen max-w-6xl bg-white border-2 border-purple-200 rounded-lg shadow-2xl z-50 p-8">
                                    <div className="grid grid-cols-4 gap-8">
                                        {/* Kategoriler */}
                                        <div>
                                            <h3 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
                                                🚗 Araç Kategorileri
                                            </h3>
                                            <ul className="space-y-2">
                                                {megaMenuData.categories.map((cat) => (
                                                    <li key={cat.path}>
                                                        <a
                                                            href={cat.path}
                                                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 px-3 py-2 rounded transition"
                                                        >
                                                            <span>{cat.icon}</span>
                                                            <span className="font-medium">{cat.name}</span>
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Mevsimler */}
                                        <div>
                                            <h3 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
                                                🌤️ Mevsimler
                                            </h3>
                                            <ul className="space-y-2">
                                                {megaMenuData.seasons.map((season) => (
                                                    <li key={season.path}>
                                                        <a
                                                            href={season.path}
                                                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 px-3 py-2 rounded transition"
                                                        >
                                                            <span>{season.icon}</span>
                                                            <span className="font-medium">{season.name}</span>
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>

                                            <h3 className="text-lg font-bold text-purple-700 mb-4 mt-6 flex items-center gap-2">
                                                ⭐ Popüler Aramalar
                                            </h3>
                                            <ul className="space-y-2">
                                                {megaMenuData.popular.map((item, idx) => (
                                                    <li key={idx}>
                                                        <a
                                                            href={item.path}
                                                            className="text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 px-3 py-2 rounded transition block"
                                                        >
                                                            {item.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Markalar - İlk 4 */}
                                        <div>
                                            <h3 className="text-lg font-bold text-purple-700 mb-4">
                                                🏭 Premium Markalar
                                            </h3>
                                            <ul className="space-y-2">
                                                {megaMenuData.brands.slice(0, 4).map((brand) => (
                                                    <li key={brand.path}>
                                                        <a
                                                            href={brand.path}
                                                            className="text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 px-3 py-2 rounded transition block font-medium"
                                                        >
                                                            {brand.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Markalar - Son 4 */}
                                        <div>
                                            <h3 className="text-lg font-bold text-purple-700 mb-4">
                                                🏆 Diğer Markalar
                                            </h3>
                                            <ul className="space-y-2">
                                                {megaMenuData.brands.slice(4).map((brand) => (
                                                    <li key={brand.path}>
                                                        <a
                                                            href={brand.path}
                                                            className="text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 px-3 py-2 rounded transition block font-medium"
                                                        >
                                                            {brand.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>

                                            <a
                                                href="/products"
                                                className="mt-6 block bg-gradient-to-r from-purple-500 to-purple-600 text-white text-center py-3 rounded-lg font-bold hover:from-purple-600 hover:to-purple-700 transition shadow-md"
                                            >
                                                Tüm Lastikleri Gör →
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Diğer menü öğeleri */}
                        {mainMenuItems.map((item) => {
                            let isActive = false;

                            if (item.path === '/') {
                                isActive = location.pathname === '/';
                            } else if (item.path.includes('?search=')) {
                                const searchParam = item.path.split('?search=')[1];
                                const decodedSearch = decodeURIComponent(location.search);
                                isActive = location.pathname === '/products' &&
                                    decodedSearch.toLowerCase().includes(searchParam.toLowerCase());
                            } else {
                                isActive = location.pathname === item.path;
                            }

                            return (
                                <button
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    className={`text-sm transition relative flex items-center gap-2 px-3 py-2 rounded-md ${isActive
                                        ? 'text-amber-700 font-bold bg-gray-100 shadow-sm'
                                        : 'text-blue-700 font-medium hover:bg-blue-50'
                                        }`}
                                >
                                    {item.icon && <span className="text-lg">{item.icon}</span>}
                                    {item.name}
                                    {isActive && (
                                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-amber-600 rounded-t-md" />
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            <BrandScroller />

            {/* Mobil Menü */}
            {isMenuOpen && (
                <>
                    <div className="lg:hidden fixed inset-0 bg-black bg-opacity-60 z-40" onClick={() => setIsMenuOpen(false)} />
                    <div className={`lg:hidden fixed top-0 right-0 h-full w-80 bg-gradient-to-b from-purple-50 to-white shadow-2xl z-50 transform transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                        <div className="flex flex-col h-full">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b-2 border-purple-200 bg-white">
                                <div className="flex items-center gap-2">
                                    <img src="/images/logo.webp" alt="Sakarya Lastik" className="h-10 w-auto object-contain" />
                                    <a href="tel:05441205454" className="flex items-center gap-2 text-purple-700 hover:text-purple-900 transition">
                                        <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-2 rounded-full shadow-sm">
                                            <FiPhone size={16} className="text-purple-600" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-gray-500 font-medium">Hemen Ara</span>
                                            <span className="font-bold text-xs text-purple-800">0(544) 120-5454</span>
                                        </div>
                                    </a>
                                </div>
                                <button onClick={() => setIsMenuOpen(false)} className="p-2 rounded-lg hover:bg-purple-100 transition">
                                    <FiX size={24} className="text-purple-600" />
                                </button>
                            </div>

                            {/* 7/24 Destek Banner */}
                            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-3 flex items-center gap-2">
                                <FiClock size={18} />
                                <span className="font-bold">7/24 Müşteri Desteği</span>
                            </div>

                            {/* Ana Menü */}
                            <nav className="flex-1 overflow-y-auto">
                                <div className="py-2">
                                    {mainMenuItems.map((item) => {
                                        let isActive = false;

                                        if (item.path === '/') {
                                            isActive = location.pathname === '/';
                                        } else if (item.path.includes('?search=')) {
                                            const searchParam = item.path.split('?search=')[1];
                                            const decodedSearch = decodeURIComponent(location.search);
                                            isActive = location.pathname === '/products' &&
                                                decodedSearch.toLowerCase().includes(searchParam.toLowerCase());
                                        } else {
                                            isActive = location.pathname === item.path;
                                        }

                                        return (
                                            <button
                                                key={item.path}
                                                onClick={() => navigate(item.path)}
                                                className={`w-full text-left px-6 py-4 transition flex items-center gap-3 ${isActive
                                                    ? 'text-purple-700 bg-purple-100 border-l-4 border-purple-600 font-bold'
                                                    : 'text-gray-700 border-l-4 border-transparent hover:bg-purple-50'
                                                    }`}
                                            >
                                                {item.icon && <span className="text-xl">{item.icon}</span>}
                                                {item.name}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Diğer Linkler */}
                                <div className="border-t-2 border-purple-100 mt-4 pt-4">
                                    <div className="px-6 py-2 text-xs font-bold text-purple-600">DİĞER</div>
                                    {topMenuItems.map((item, index) => (
                                        <button
                                            key={index}
                                            onClick={() => navigate(item.path)}
                                            className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-purple-50 transition"
                                        >
                                            {item.name}
                                        </button>
                                    ))}
                                </div>

                                {/* Sosyal Medya */}
                                <div className="border-t-2 border-purple-100 mt-4 pt-4">
                                    <div className="px-6 py-2 text-xs font-bold text-purple-600">SOSYAL MEDYA</div>
                                    <div className="px-6 py-3 flex gap-4">
                                        {socialLinks.map((social, index) => (
                                            <a
                                                key={index}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-2xl text-purple-600 hover:text-purple-800 transition transform hover:scale-125"
                                                title={social.name}
                                            >
                                                {social.icon}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </nav>

                            {/* İletişim Footer */}
                            <div className="border-t-2 border-purple-200 p-4 bg-gradient-to-b from-white to-purple-50">
                                <div className="text-xs font-bold text-purple-600">İLETİŞİM</div>
                                <a href="tel:05441205454" className="flex items-center text-gray-700 hover:text-purple-700 transition p-2 rounded-lg hover:bg-purple-100">
                                    <div className="bg-purple-100 p-2 rounded-full">
                                        <FiPhone size={16} className="text-purple-600" />
                                    </div>
                                    <span className="text-sm font-bold">0(544) 120-5454</span>
                                </a>
                                <a href="tel:05357455407" className="flex items-center text-gray-700 hover:text-purple-700 transition p-2 rounded-lg hover:bg-purple-100">
                                    <div className="bg-purple-100 p-2 rounded-full">
                                        <FiPhone size={16} className="text-purple-600" />
                                    </div>
                                    <span className="text-sm font-bold">0(535) 745-5407</span>
                                </a>
                                <a href="mailto:info@sakaryalastik.com" className="flex items-center text-gray-700 hover:text-purple-700 transition p-2 rounded-lg hover:bg-purple-100">
                                    <div className="bg-purple-100 p-2 rounded-full">
                                        <FiMail size={16} className="text-purple-600" />
                                    </div>
                                    <span className="text-sm">info@sakaryalastik.com</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </header>
    )
}

export default Navbar